import React, { useState, useEffect } from "react";
import Header from "../../../components/UserNavbar/UserNavbar";
import Footer from "../../../components/UserFooter/UserFooter";
import {
    Container,
    Box,
    Typography,
    Button,
    Checkbox,
    Grid,
    Divider,
    Snackbar,
    Alert,
    CircularProgress,
    Modal,
    TextField,
    Card,
    CardContent,
    FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { storeUserAcceptance, updateUserByMobile, checkCampaignCode, firstTimeLogin } from "../../../api/api"; // Adjust path as needed
import { useLocation } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const Index = () => {
    const navigate = useNavigate();
    const [isAccepted, setIsAccepted] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userDetails, setUserDetails] = useState({ email: "" });
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [campaignDetails, setCampaignDetails] = useState(null);
    const [campaignAcceptanceChecked, setCampaignAcceptanceChecked] = useState(false);
    const [processing, setProcessing] = useState(false);
    const { state } = useLocation();
    const mobile = state?.mobile;
    const company_id = state?.company_id || Cookies.get("company_id"); // Get from state or cookies
    const campaignCode = state?.campaign_code; // Get campaign code from state
    const mobileNumber = mobile; // Use mobile from state instead of cookie

    useEffect(() => {
        let userId = Cookies.get("user_id");
        let companyId = Cookies.get("company_id");
        
        // If cookies not set but we have state from registration/login, set cookies from state
        if ((!userId || !companyId) && state?.company_id) {
            if (state?.user_id) {
                userId = state.user_id;
                Cookies.set("user_id", userId, {
                    expires: 90,
                    path: '/',
                    sameSite: 'lax'
                });
            }
            if (state?.company_id) {
                companyId = state.company_id;
                Cookies.set("company_id", companyId, {
                    expires: 90,
                    path: '/',
                    sameSite: 'lax'
                });
            }
        }
        
        if (!mobileNumber) {
            navigate("/");
            return;
        }

        // Check if this is a first-time user (hasn't accepted yet)
        const checkFirstTime = async () => {
            try {
                const firsttimeCheck = await firstTimeLogin({
                    user: mobileNumber,
                });

                // If user has already accepted before, redirect to home
                if (!firsttimeCheck?.data?.first_time) {
                    const finalCompanyId = companyId || state?.company_id || Cookies.get("company_id");
                    if (finalCompanyId) {
                        navigate("/user_home", {
                            state: {
                                mobile: mobileNumber,
                                company_id: finalCompanyId,
                            }
                        });
                    } else {
                        // No company ID - redirect to login
                        navigate("/");
                    }
                    return;
                }

                // Fetch campaign details if campaign code is available
                if (campaignCode) {
                    try {
                        const response = await checkCampaignCode(campaignCode);
                        if (response.data.valid) {
                            setCampaignDetails(response.data.campaign);
                        }
                    } catch (error) {
                        console.error("Error fetching campaign details:", error);
                    }
                }
                
                setLoading(false);
            } catch (err) {
                console.error("Login check error", err);
                // On error, still show the acceptance form
                setLoading(false);
            }
        };

        checkFirstTime();
    }, [mobileNumber, campaignCode, company_id, navigate]);

    const handleAccept = async () => {
        if (!isAccepted) {
            setOpenSnackbar(true);
            return;
        }

        // if (campaignCode && !campaignAcceptanceChecked) {
        //     setOpenSnackbar(true);
        //     return;
        // }

        setProcessing(true);

        try {
            // Store user acceptance
            const userId = Cookies.get("user_id"); // Get actual user ID from cookie
            
            if (!userId) {
                console.error("User ID not found in cookies");
                setProcessing(false);
                return;
            }
            
            await storeUserAcceptance({ user_id: userId });
            
            // Ensure we have company_id - get from cookies if not in state
            const finalCompanyId = company_id || Cookies.get("company_id");
            
            if (!finalCompanyId) {
                console.error("Company ID not found");
                setProcessing(false);
                return;
            }
            
            // Ensure cookies are set before navigating
            if (!Cookies.get("user_id") || !Cookies.get("company_id")) {
                Cookies.set("user_id", userId, {
                    expires: 90,
                    path: '/',
                    sameSite: 'lax'
                });
                Cookies.set("company_id", finalCompanyId, {
                    expires: 90,
                    path: '/',
                    sameSite: 'lax'
                });
            }
            
            // Navigate to user home
            navigate("/user_home", { 
                state: { 
                    mobile: mobile, 
                    company_id: finalCompanyId,
                    message: "Velkommen! Du har nå tilgang til kuponger og poeng."
                } 
            });
        } catch (err) {
            console.error("Error storing user acceptance", err);
            setProcessing(false);
        }
    };

    // Email handling functions
    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        setEmailError("");
    };

    const handleAddEmail = async () => {
        if (!email.trim()) {
            setEmailError("Vennligst skriv inn en gyldig e-postadresse");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError("Vennligst skriv inn en gyldig e-postadresse");
            return;
        }

        setEmailLoading(true);
        setEmailError("");

        try {
            const userId = Cookies.get("user_id");
            await updateUserByMobile(mobileNumber, {
                email: email.trim(),
                company_id: company_id || Cookies.get("company_id"),
                user_id: userId // Include user_id for authorization
            });

            setUserDetails(prev => ({ ...prev, email: email.trim() }));
            setEmailModalOpen(false);
            setEmail("");
        } catch (error) {
            setEmailError("Kunne ikke oppdatere e-post. Prøv igjen.");
        } finally {
            setEmailLoading(false);
        }
    };

    const handleReject = async () => {
        setProcessing(true);

        try {
            // DON'T remove user from campaign - they should stay in campaign
            // They just haven't accepted terms yet
            // When they login again, they'll see acceptance page and can then access coupons
            // Removing them would cause login issues (no company associations)
            
            // Clear cookies and redirect to login
            // User account and campaign membership stay in database
            // Next time they login, they'll see acceptance page (because no user_login record)
            Cookies.remove("user_id");
            Cookies.remove("company_id");
            navigate("/");
        } catch (err) {
            console.error("Error during cancel:", err);
            // Still redirect to login even if error
            Cookies.remove("user_id");
            Cookies.remove("company_id");
            navigate("/");
        }
    };

    // if (loading) {
    //     return (
    //         <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
    //             <CircularProgress />
    //         </Box>
    //     );
    // }

    return (
        <>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setOpenSnackbar(false)}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    Vennligst godta vilkårene for å fortsette.
                </Alert>
            </Snackbar>
            <Header />
            <Container sx={{ maxWidth: "sm", mx: "auto", mt: 2 }}>
                <Box mb={3}>
                <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        py: 1.5
                    }}>
                        <Typography sx={{ 
                            fontSize: { xs: "0.9rem", sm: "1rem" }, 
                            minWidth: '120px',
                            fontWeight: 500
                        }}>
                            Mobil/BrukerID:
                        </Typography>
                        <Typography sx={{ 
                            fontSize: { xs: "0.9rem", sm: "1rem" }, 
                            textAlign: 'center', 
                            flex: 1,
                            fontWeight: 400
                        }}>
                            {mobile}
                        </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        py: 1.5
                    }}>
                        <Typography sx={{ 
                            fontSize: { xs: "0.9rem", sm: "1rem" }, 
                            minWidth: '120px',
                            fontWeight: 500
                        }}>
                            E-post:
                        </Typography>
                        <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {userDetails.email && (
                                    <Typography 
                                        sx={{ 
                                            color: 'primary.main', 
                                            cursor: 'pointer', 
                                            fontSize: '1rem',
                                           // textDecoration: 'underline'
                                        }}
                                        onClick={() => {
                                            setEmail(userDetails.email);
                                            setEmailModalOpen(true);
                                        }}
                                    >
                                      {userDetails.email ? userDetails.email : "Legg til e-post"} (rediger)
                                    </Typography>
                                )}
                                {!userDetails.email && (
                                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                                        <Button 
                                            variant="outlined" 
                                            size="small"
                                            onClick={() => setEmailModalOpen(true)}
                                            sx={{ fontSize: '0.75rem' }}
                                        >
                                            Legg til e-post
                                        </Button>
                                    </Box>
                                )}
                        </Box>
                    </Box>
                </Box>

                {/* Campaign Details Section */}
                {campaignDetails && (
                    <Card sx={{ mt: 3, mb: 3 }}>
                        <CardContent>
                            <Box textAlign="center" sx={{ mb: 3 }}>
                                <Typography 
                                    variant="h5" 
                                    gutterBottom 
                                    sx={{ color: 'primary.main', fontWeight: 'bold' }}
                                >
                                    🎉 Kampanje Tilbud!
                                </Typography>
                                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                    {campaignDetails.name}
                                </Typography>
                                {campaignDetails.partner && (
                                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                                        Fra: {campaignDetails.partner}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                    Hva får du?
                                </Typography>
                                
                                {/* Show ValueBank Points if available */}
                                {(campaignDetails.value_points > 0 || campaignDetails.variable_points > 0) && (
                                    <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                                            💰 Verdibank Poeng
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'success.dark' }}>
                                            {campaignDetails.value_points > 0 && `${campaignDetails.value_points} poeng`}
                                            {campaignDetails.variable_points && campaignDetails.value_points > 0 && ' + '}
                                            {campaignDetails.variable_points && 'Variable poeng'}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Show Coupons if available */}
                                {campaignDetails.coupon_initials_ids && (
                                    <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'info.dark' }}>
                                            🎫 Kuponger
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'info.dark' }}>
                                            {campaignDetails.coupon_initials_ids.split(',').length} kupong(er) tilgjengelig
                                        </Typography>
                                    </Box>
                                )}

                                {/* Campaign Details */}
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Kampanje Kode:</strong> {campaignDetails.code}
                                    </Typography>
                                    {campaignDetails.expire_days && (
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Gyldig i:</strong> {campaignDetails.expire_days} dager
                                        </Typography>
                                    )}
                                    {campaignDetails.end_date && (
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Utløper:</strong> {new Date(campaignDetails.end_date).toLocaleDateString('no-NO')}
                                        </Typography>
                                    )}
                                    {campaignDetails.signup_end_date && (
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Påmeldingsfrist:</strong> {new Date(campaignDetails.signup_end_date).toLocaleDateString('no-NO')}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Campaign Acceptance Checkbox */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
                                    Ved å godta dette tilbudet vil du få tilgang til kuponger og poeng.
                                </Typography>
                                
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    mb: 2,
                                    p: 2,
                                    border: campaignAcceptanceChecked ? '2px solid #4caf50' : '2px solid #e0e0e0',
                                    borderRadius: 2,
                                    backgroundColor: campaignAcceptanceChecked ? '#f1f8e9' : '#fafafa',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={campaignAcceptanceChecked}
                                                onChange={(e) => setCampaignAcceptanceChecked(e.target.checked)}
                                                color="primary"
                                                size="large"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: campaignAcceptanceChecked ? 'bold' : 'normal' }}>
                                                ✅ Jeg godtar å bli med i denne kampanjen og forstår at jeg vil motta kuponger og poeng
                                            </Typography>
                                        }
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                <Box sx={{ textAlign: "center", mt: 4 }}>
                    <Typography variant="h4">Aksept for å fortsette</Typography>
                </Box>

                <Typography sx={{ mt: 2, color: "secondary" }}>
                    Vi er avhengig av ditt samtykke for å kunne kommunisere med
                    deg. Vi tar ditt personvern på alvor, og vil aldri dele dine
                    personopplysninger med tredjeparter. Se vår
                    personvernerklæring for mer informasjon.
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
                    <Checkbox
                        checked={isAccepted}
                        onChange={(e) => setIsAccepted(e.target.checked)}
                    />
                    <Typography fontWeight={550}>
                        Jeg godtar å bruke og motta digital kommunikasjon fra
                        Fargerike Majorstuen.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "center",
                        mt: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAccept}
                        disabled={processing}
                        startIcon={processing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    >
                        {processing ? 'Behandler...' : 'GODKJENN'}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleReject}
                        disabled={processing}
                    >
                        {processing ? 'Behandler...' : 'IKKE GODKJENNE'}
                    </Button>
                </Box>

                <Box sx={{ height: "100px" }}></Box>
            </Container>
            <Footer />

            {/* Email Modal */}
            <Modal
                open={emailModalOpen}
                onClose={() => {
                    setEmailModalOpen(false);
                    setEmail("");
                    setEmailError("");
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Box sx={{
                    bgcolor: 'background.paper',
                    p: 3,
                    borderRadius: 2,
                    width: '90%',
                    maxWidth: 400,
                    outline: 'none'
                }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        {userDetails.email ? "Rediger e-post" : "Legg til e-post"}
                    </Typography>
                    
                    <TextField
                        fullWidth
                        label="E-postadresse"
                        variant="outlined"
                        value={email}
                        onChange={handleEmailChange}
                        error={!!emailError}
                        helperText={emailError}
                        sx={{ mb: 2 }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setEmailModalOpen(false);
                                setEmail("");
                                setEmailError("");
                            }}
                        >
                            Avbryt
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleAddEmail}
                            disabled={emailLoading}
                        >
                            {emailLoading ? "Oppdaterer..." : (userDetails.email ? "Oppdater" : "Legg til")}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default Index;
